import React from 'react';

const DEFAULT_ALIGNER_PROGRESS_SIZE = 24;
const DEFAULT_ALIGNER_PROGRESS_STROKE = 2;

interface AlignerProgressProps {
    current: number;
    total: number;
    size?: number;
    strokeWidth?: number;
    className?: string;
}

export const AlignerProgress: React.FC<AlignerProgressProps> = ({
    current,
    total,
    size = DEFAULT_ALIGNER_PROGRESS_SIZE,
    strokeWidth = DEFAULT_ALIGNER_PROGRESS_STROKE,
    className
}) => {
    const clampedTotal = Number.isFinite(total) ? Math.max(0, total) : 0;
    const clampedCurrent = Number.isFinite(current)
        ? Math.max(0, Math.min(current, clampedTotal || 0))
        : 0;
    const radius = Math.max((size - strokeWidth) / 2, 0);
    const circumference = 2 * Math.PI * radius;
    const progressRatio = clampedTotal > 0 ? Math.min(1, clampedCurrent / clampedTotal) : 0;
    const progressPercent = Math.round(progressRatio * 100);
    const dashoffset = circumference * (1 - progressRatio);
    const rootClassName = ['relative flex items-center justify-center', className]
        .filter(Boolean)
        .join(' ');
    const currentLabel = clampedTotal > 0 ? String(clampedCurrent) : '0';
    const totalLabel = clampedTotal > 0 ? String(clampedTotal) : '0';

    return (
        <div
            className={rootClassName}
            aria-label={`Aligner ${currentLabel} of ${totalLabel}, ${progressPercent}% complete`}
        >
            <svg className="h-full w-full" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    className="text-gray-200"
                    stroke="currentColor"
                    fill="transparent"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    className="text-emerald-500 transition-[stroke-dashoffset] duration-300"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashoffset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <span className="absolute text-xs font-semibold leading-4 text-gray-700">
                {currentLabel}/{totalLabel}
            </span>
        </div>
    );
};
